import axios from 'axios';

// Configure base URL
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Configure axios to always send credentials (cookies)
axios.defaults.withCredentials = true;

// Flag to prevent multiple session end alerts
let sessionEndedAlertShown = false;

// Session health check interval
let healthCheckInterval = null;

// Start periodic session health check
export function startSessionHealthCheck() {
    if (healthCheckInterval) return; // Already running

    healthCheckInterval = setInterval(async () => {
        try {
            await axios.get('/api/auth/verify-session');
        } catch (error) {
            // Error will be handled by the interceptor
        }
    // 5 min: this is only a slow safety net. Remote session termination is
    // pushed instantly over the /api/sessions/stream SSE connection; polling
    // every 30s doubled that work for no benefit (2 req/min per open tab).
    }, 5 * 60 * 1000);
}

// Stop the health check
export function stopSessionHealthCheck() {
    if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;
    }
}

// Reset session ended flag on new login
export function resetSessionAlert() {
    sessionEndedAlertShown = false;
}

// ---- Global 429 (rate limit) backoff -------------------------------------
// When the API says "too many requests", pause ALL outgoing axios requests
// for the advised time instead of letting every component retry on its own
// schedule. Independent retries are what kept the rate limiter tripped during
// the 2026-07-21 outage (site appeared down until a server restart).
let rateLimitedUntil = 0;

export function isRateLimited() {
    return Date.now() < rateLimitedUntil;
}

// Block requests while in backoff. Rejects with a synthetic 429-shaped error
// so existing `error.response?.status === 429` handling works unchanged.
axios.interceptors.request.use((config) => {
    if (Date.now() < rateLimitedUntil) {
        const error = new Error('Rate limited — client backoff active');
        error.response = { status: 429, data: { message: 'Too many requests, please wait.' } };
        error.isClientBackoff = true;
        return Promise.reject(error);
    }
    return config;
});

// Add axios interceptor to handle token expiration
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If session was ended remotely, immediately redirect to login
        if (error.response?.status === 401 && error.response?.data?.code === 'SESSION_ENDED') {
            // Don't hijack the "secure my account" hand-off. Clicking
            // "No, this wasn't me" intentionally ends every session, which
            // makes this same tab see SESSION_ENDED. If we force-redirect to
            // /login here, we abort the redirect to the set-a-new-password
            // page. On those routes, let the flow finish on its own.
            const hash = window.location.hash || '';
            const inSecureFlow = hash.startsWith('#/security-alert') || hash.startsWith('#/reset-password');
            if (inSecureFlow) {
                return Promise.reject(error);
            }
            // Show alert to user only once
            if (!sessionEndedAlertShown) {
                sessionEndedAlertShown = true;
                console.error(error.response.data.message || 'Your session has been ended. Please log in again.');
                // Force a full page reload to reset React state and redirect to login
                setTimeout(() => {
                    window.location.href = '/#/login';
                    window.location.reload();
                }, 100);
            }
            return Promise.reject(error);
        }

        // If we get a 401 error with TOKEN_EXPIRED code, try to refresh
        if (error.response?.status === 401 &&
            error.response?.data?.code === 'TOKEN_EXPIRED' &&
            !originalRequest._retry) {

            originalRequest._retry = true;

            try {
                // Try to refresh the token
                await axios.post('/api/auth/refresh');

                // Retry the original request
                return axios(originalRequest);
            } catch (refreshError) {
                // Refresh failed, redirect to login
                window.location.href = '/#/login';
                return Promise.reject(refreshError);
            }
        }

        // Rate limited by the server: start (or extend) the global backoff.
        // Uses the server's Retry-After header when present, else 30s, capped
        // at 2 min. A window event is emitted so UI can show a banner if we
        // ever want one; components already handle 429 errors individually.
        if (error.response?.status === 429 && !error.isClientBackoff) {
            const retryAfter = parseInt(error.response.headers?.['retry-after'], 10);
            const waitMs = (Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter, 120) : 30) * 1000;
            const until = Date.now() + waitMs;
            if (until > rateLimitedUntil) {
                rateLimitedUntil = until;
                console.warn(`API rate limited — pausing all requests for ${waitMs / 1000}s`);
                window.dispatchEvent(new CustomEvent('api-rate-limited', { detail: { until } }));
            }
        }

        return Promise.reject(error);
    }
);

export default axios;
